"use client";

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Send,
  CheckCircle,
  XCircle,
  RotateCcw,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { updateProposalStatus } from "@/lib/api/proposals";
import { getErrorMessage } from "@/lib/api/client";
import type { ProposalStatus } from "@/types";

interface StatusWorkflowProps {
  proposalId: number;
  currentStatus: ProposalStatus;
  onStatusChange?: (newStatus: ProposalStatus) => void;
}

interface StatusTransition {
  from: ProposalStatus;
  to: ProposalStatus;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: "default" | "destructive" | "outline" | "success";
  confirmTitle: string;
  confirmDescription: string;
}

const statusTransitions: StatusTransition[] = [
  {
    from: "draft",
    to: "sent",
    label: "Envoyer au client",
    icon: Send,
    variant: "default",
    confirmTitle: "Envoyer la proposition ?",
    confirmDescription:
      "Une fois envoyée, la proposition ne pourra plus être modifiée sauf par un administrateur.",
  },
  {
    from: "sent",
    to: "draft",
    label: "Repasser en brouillon",
    icon: RotateCcw,
    variant: "outline",
    confirmTitle: "Repasser en brouillon ?",
    confirmDescription:
      "La proposition pourra à nouveau être modifiée.",
  },
  {
    from: "sent",
    to: "accepted",
    label: "Marquer comme acceptée",
    icon: CheckCircle,
    variant: "success",
    confirmTitle: "Accepter la proposition ?",
    confirmDescription:
      "Cette action indique que le client a accepté la proposition.",
  },
  {
    from: "sent",
    to: "rejected",
    label: "Marquer comme refusée",
    icon: XCircle,
    variant: "destructive",
    confirmTitle: "Refuser la proposition ?",
    confirmDescription:
      "Cette action indique que le client a refusé la proposition.",
  },
  {
    from: "rejected",
    to: "draft",
    label: "Réouvrir en brouillon",
    icon: RotateCcw,
    variant: "outline",
    confirmTitle: "Réouvrir la proposition ?",
    confirmDescription:
      "La proposition pourra être modifiée et renvoyée au client.",
  },
];

export function StatusWorkflow({
  proposalId,
  currentStatus,
  onStatusChange,
}: StatusWorkflowProps) {
  const [confirmDialog, setConfirmDialog] = useState<StatusTransition | null>(null);
  const queryClient = useQueryClient();

  const statusMutation = useMutation({
    mutationFn: (newStatus: ProposalStatus) =>
      updateProposalStatus(proposalId, { status: newStatus }),
    onSuccess: (data) => {
      toast.success("Statut mis à jour");
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      setConfirmDialog(null);
      if (onStatusChange) {
        onStatusChange(data.status);
      }
    },
    onError: (error) => {
      toast.error("Erreur", {
        description: getErrorMessage(error),
      });
    },
  });

  // Get available transitions for current status
  const availableTransitions = statusTransitions.filter(
    (t) => t.from === currentStatus
  );

  // If accepted status, no transitions available
  if (currentStatus === "accepted") {
    return null;
  }

  if (availableTransitions.length === 0) {
    return null;
  }

  // For draft, show primary action button
  if (currentStatus === "draft") {
    const sendTransition = availableTransitions.find((t) => t.to === "sent");
    if (!sendTransition) return null;

    const Icon = sendTransition.icon;
    return (
      <>
        <Button onClick={() => setConfirmDialog(sendTransition)}>
          <Icon className="h-4 w-4 mr-2" />
          {sendTransition.label}
        </Button>

        <StatusConfirmDialog
          transition={confirmDialog}
          isLoading={statusMutation.isPending}
          onConfirm={() => statusMutation.mutate(confirmDialog!.to)}
          onCancel={() => setConfirmDialog(null)}
        />
      </>
    );
  }

  // For other statuses, show dropdown
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            Changer le statut
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {availableTransitions.map((transition, idx) => {
            const Icon = transition.icon;
            return (
              <DropdownMenuItem
                key={transition.to}
                onClick={() => setConfirmDialog(transition)}
                className={
                  transition.variant === "destructive"
                    ? "text-destructive"
                    : transition.variant === "success"
                    ? "text-success"
                    : ""
                }
              >
                <Icon className="h-4 w-4 mr-2" />
                {transition.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <StatusConfirmDialog
        transition={confirmDialog}
        isLoading={statusMutation.isPending}
        onConfirm={() => statusMutation.mutate(confirmDialog!.to)}
        onCancel={() => setConfirmDialog(null)}
      />
    </>
  );
}

interface StatusConfirmDialogProps {
  transition: StatusTransition | null;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function StatusConfirmDialog({
  transition,
  isLoading,
  onConfirm,
  onCancel,
}: StatusConfirmDialogProps) {
  if (!transition) return null;

  const Icon = transition.icon;

  return (
    <Dialog open={!!transition} onOpenChange={() => onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {transition.confirmTitle}
          </DialogTitle>
          <DialogDescription>
            {transition.confirmDescription}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            variant={transition.variant === "destructive" ? "destructive" : "default"}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
